import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'

export default function Login() {
  const [searchParams] = useSearchParams()
  const emailFromUrl = searchParams.get('email')
  const isVerified = searchParams.get('verified') === 'true'
  
  const [email, setEmail] = useState(emailFromUrl || '')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [showVerifiedMessage, setShowVerifiedMessage] = useState(isVerified)
  const { signIn } = useAuth()
  const navigate = useNavigate()
  
  // Hide verified message after 5 seconds
  useEffect(() => {
    if (showVerifiedMessage) {
      const timer = setTimeout(() => setShowVerifiedMessage(false), 5000)
      return () => clearTimeout(timer)
    }
  }, [showVerifiedMessage])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      console.log('Attempting login for:', email)
      const { data, error: signInError } = await signIn(email, password)
      
      console.log('Login response:', { data, error: signInError })

      if (signInError) {
        console.error('Login error:', signInError)
        // Better error messages
        if (signInError.message.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please try again.')
        } else if (signInError.message.includes('Email not confirmed')) {
          setError('Please confirm your email address before logging in.')
        } else {
          setError(signInError.message || 'Login failed. Please try again.')
        }
        setLoading(false)
      } else {
        console.log('Login successful!')
        
        // Play hydraulic door sound (same as game creation)
        try {
          const doorSound = new Audio('/assets/audio/hydraulic-door.mp3');
          doorSound.volume = 0.6; // 60% volume for impactful sound
          doorSound.play().catch(err => console.debug('Door sound blocked:', err));
        } catch (err) {
          console.debug('Door sound error:', err);
        }
        
        // Start cinematic transition
        setIsTransitioning(true)
        
        // Wait 3 seconds to show the background art, then navigate
        setTimeout(() => {
          navigate('/')
        }, 3000)
      }
    } catch (err) {
      console.error('Unexpected error during login:', err)
      setError('An unexpected error occurred. Please try again.')
      setLoading(false)
    }
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
    >
      {/* Animated Video Background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className={`fixed inset-0 w-full h-full object-cover transition-all duration-1000`}
        style={{ 
          zIndex: 0,
          filter: isTransitioning ? 'brightness(1.2)' : 'brightness(1.0)'
        }}
      >
        <source src="/assets/world-leaders-nuke.mp4" type="video/mp4" />
        {/* Fallback to static image if video fails to load */}
        <img 
          src="/assets/nuke-ride-bg.png" 
          alt="Background"
          className="w-full h-full object-cover"
        />
      </video>

      {/* Dark overlay for readability - fade out during transition */}
      <div 
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-1000 ${
          isTransitioning ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ zIndex: 1 }}
      />
      
      {/* Content - fade out during transition */}
      <div 
        className={`relative bg-slate-900/95 backdrop-blur-md p-8 rounded-lg w-full max-w-md border-4 border-blue-500/50 shadow-2xl transition-opacity duration-1000 ${
          isTransitioning ? 'opacity-0' : 'opacity-100'
        }`}
        style={{
          zIndex: 2,
          boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2), inset 0 2px 20px rgba(0,0,0,0.5)'
        }}
      >
        <h1 className="text-4xl font-bold text-center mb-2 font-mono tracking-wider"
          style={{
            background: 'linear-gradient(to right, #60a5fa, #a78bfa)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0 20px rgba(96, 165, 250, 0.5)',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.9))',
          }}
        >
          Global Thermo III
        </h1>
        <p className="text-center text-slate-300 mb-8 text-sm tracking-wide">Login to Command Your Nation</p>

        {/* Verified Success Message */}
        {showVerifiedMessage && (
          <div className="mb-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-400 rounded-lg p-4 animate-pulse">
            <div className="text-center">
              <div className="text-3xl mb-2">✅ 🎉</div>
              <h3 className="text-lg font-bold text-green-300 mb-1">Email Verified!</h3>
              <p className="text-slate-300 text-sm">
                Your bonuses are unlocked. Please login below.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-200 mb-2 tracking-wide">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 bg-slate-800/80 border-2 border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 text-white placeholder-slate-500 transition-all font-mono"
              style={{
                boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.4)'
              }}
              placeholder="commander@nation.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-200 mb-2 tracking-wide">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 bg-slate-800/80 border-2 border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 text-white placeholder-slate-500 transition-all font-mono"
              style={{
                boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.4)'
              }}
              placeholder="••••••••"
            />
            {/* Forgot Password Link */}
            <div className="text-right mt-2">
              <Link 
                to="/forgot-password" 
                className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                style={{ textShadow: '0 0 10px rgba(59, 130, 246, 0.5)' }}
              >
                Forgot password?
              </Link>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold py-4 px-4 rounded-lg hover:from-blue-500 hover:to-purple-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wider text-lg border-2 border-blue-400/50 relative overflow-hidden group"
            style={{
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.5), 0 4px 12px rgba(0,0,0,0.5)',
              textShadow: '0 2px 4px rgba(0,0,0,0.5)'
            }}
          >
            <span className="relative z-10">{loading ? 'Logging in...' : 'Login'}</span>
            <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 opacity-0 group-hover:opacity-30 transition-opacity" />
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-slate-300 text-sm">
            Don't have an account?{' '}
            <Link 
              to="/signup" 
              className="text-blue-400 hover:text-blue-300 font-bold tracking-wide transition-colors"
              style={{ textShadow: '0 0 10px rgba(59, 130, 246, 0.5)' }}
            >
              Sign Up
            </Link>
          </p>
        </div>
      </div>
      
      {/* Animated glow effects */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDelay: '1s' }} />
    </div>
  )
}
